// 判断字符是否为可打字字符（字母、数字、空格）
export function isTypableChar(char: string): boolean {
  return /^[a-zA-Z0-9\s]$/.test(char);
}

// 判断字符是否为标点符号（打字时自动跳过）
export function isPunctuation(char: string): boolean {
  // 使用 Unicode 范围匹配中英文标点符号
  const punctuationRegex = /[\u3000-\u303F\uFF00-\uFF65\u2000-\u206F\\,.!?;:'"()\[\]{}\-–—…·'']/;
  return punctuationRegex.test(char);
}

// 判断字符是否需要跳过（仅标点符号，不包括空格）
// 空格需要正常输入
export function isSkippable(char: string): boolean {
  return isPunctuation(char);
}

// 智能拆分文本为行
export function smartWrapText(text: string, maxLineLength: number = 60): string[] {
  const lines: string[] = [];

  // 首先按原有换行拆分
  const paragraphs = text.split('\n');

  for (const paragraph of paragraphs) {
    if (paragraph.length <= maxLineLength) {
      lines.push(paragraph);
      continue;
    }

    let currentLine = '';
    let lastPunctIndex = -1;

    for (let i = 0; i < paragraph.length; i++) {
      const char = paragraph[i];

      if (isPunctuation(char)) {
        lastPunctIndex = currentLine.length;
      }

      currentLine += char;

      // 到达最大长度，尝试在标点处断开
      if (currentLine.length >= maxLineLength) {
        if (lastPunctIndex > 0 && lastPunctIndex < currentLine.length - 5) {
          // 在标点处断开
          lines.push(currentLine.slice(0, lastPunctIndex + 1));
          currentLine = currentLine.slice(lastPunctIndex + 1);
          lastPunctIndex = -1;
        } else {
          // 找不到合适标点，强制断开
          lines.push(currentLine);
          currentLine = '';
          lastPunctIndex = -1;
        }
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines;
}

// 过滤掉标点符号，只保留可打字字符
export function filterTypableText(text: string): string {
  return text.split('').filter(isTypableChar).join('');
}

// 计算目标文本中的可打字字符数（不包括标点，但包括空格）
export function countTypableChars(text: string): number {
  return text.split('').filter(char => !isPunctuation(char)).length;
}

// 将输入字符映射到目标位置（跳过标点）
export function mapInputToTarget(target: string, input: string): { char: string; status: 'correct' | 'error' | 'pending' }[] {
  const result: { char: string; status: 'correct' | 'error' | 'pending' }[] = [];
  let inputIndex = 0;

  for (let i = 0; i < target.length; i++) {
    const targetChar = target[i];

    if (isSkippable(targetChar)) {
      // 标点符号自动标记为正确，不消耗输入
      result.push({ char: targetChar, status: 'correct' });
    } else {
      if (inputIndex < input.length) {
        const inputChar = input[inputIndex];
        result.push({
          char: targetChar,
          status: inputChar === targetChar ? 'correct' : 'error'
        });
        inputIndex++;
      } else {
        result.push({ char: targetChar, status: 'pending' });
      }
    }
  }

  return result;
}

// 计算错误数（只比较可打字字符，不包括标点，但包括空格）
export function calculateErrors(target: string, input: string): number {
  let errors = 0;
  let inputIndex = 0;

  for (let i = 0; i < target.length; i++) {
    const targetChar = target[i];

    if (isPunctuation(targetChar)) {
      continue; // 跳过标点符号
    }

    if (inputIndex < input.length) {
      if (input[inputIndex] !== targetChar) {
        errors++;
      }
      inputIndex++;
    }
  }

  return errors;
}

// 获取当前应该输入的位置（考虑标点跳过）
export function getCurrentInputIndex(target: string, inputLength: number): number {
  let typableCount = 0;

  for (let i = 0; i < target.length; i++) {
    if (isPunctuation(target[i])) {
      continue;
    }
    if (typableCount === inputLength) {
      return i;
    }
    typableCount++;
  }

  return target.length;
}

// 将用户输入转换为显示格式（带标点和正确大小写）
export function formatDisplayInput(target: string, userInput: string): string {
  let userIndex = 0;
  let result = '';

  for (let i = 0; i < target.length; i++) {
    const targetChar = target[i];

    if (isPunctuation(targetChar)) {
      // 标点符号自动插入
      result += targetChar;
    } else if (userIndex < userInput.length) {
      // 使用目标的大小写
      const userChar = userInput[userIndex];
      // 如果用户输入的字符（忽略大小写）匹配目标，使用目标的大小写
      if (userChar.toLowerCase() === targetChar.toLowerCase()) {
        result += targetChar; // 使用目标的大小写
      } else {
        result += userChar; // 用户输错了，显示用户输入
      }
      userIndex++;
    } else {
      // 还没输入到这儿，停止
      break;
    }
  }

  return result;
}

// 大小写不敏感的错误计算
export function calculateErrorsCaseInsensitive(target: string, input: string): number {
  let errors = 0;
  let inputIndex = 0;

  for (let i = 0; i < target.length; i++) {
    const targetChar = target[i];

    if (isPunctuation(targetChar)) {
      continue; // 跳过标点
    }

    if (inputIndex < input.length) {
      const inputChar = input[inputIndex];
      // 大小写不敏感比较
      if (inputChar.toLowerCase() !== targetChar.toLowerCase()) {
        errors++;
      }
      inputIndex++;
    }
  }

  return errors;
}

// 从显示input中提取用户实际输入（去掉标点）
// 注意：displayInput 包含自动插入的标点，需要找出哪些字符对应用户的实际输入
export function extractRawInput(target: string, displayInput: string): string {
  let raw = '';
  let targetIndex = 0;
  let displayIndex = 0;

  while (displayIndex < displayInput.length && targetIndex < target.length) {
    const targetChar = target[targetIndex];
    const displayChar = displayInput[displayIndex];

    if (isPunctuation(targetChar)) {
      // 目标位置的标点是自动插入的，跳过这个目标位置和显示位置
      targetIndex++;
      displayIndex++;
    } else {
      // 这是一个用户输入的字符（或者错误字符）
      raw += displayChar;
      targetIndex++;
      displayIndex++;
    }
  }

  // 如果还有剩余的显示字符（用户可能输入了额外的内容），也添加进去
  while (displayIndex < displayInput.length) {
    raw += displayInput[displayIndex];
    displayIndex++;
  }

  return raw;
}
